import {
  formatDashboardBullets,
  formatExecutiveSummary,
  formatExpertComment,
  limitLines,
  limitWords,
} from './vessel-insights-format.util';
import {
  BenchmarkComparisonView,
  deriveAiConfidence,
  deriveInsightTags,
  deriveScoreBreakdown,
  ExtractionQualityMeta,
  InsightTagView,
  parseBenchmarkComparison,
  ScoreBreakdownItem,
} from './vessel-insights-metrics.util';

export const EXPERT_COMMENT_FALLBACK =
  'Detailed observations unavailable from extracted report data.';

export interface VesselInsightSignals {
  tcpVariancePct: number | null;
  baselineVariancePct: number | null;
  idleHours: number | null;
  portStayHours: number | null;
  cargoVariancePct: number | null;
  manoeuvringVariancePct: number | null;
}

export interface NormalizedVesselInsights {
  executive_summary: string;
  ai_insights: string[];
  guidance: string[];
  chief_engineer: string;
  chief_engineer_full: string;
  technical_superintendent: string;
  technical_superintendent_full: string;
  naval_architect: string;
  naval_architect_full: string;
  operational_score: number;
  ai_confidence: number;
  risk_indicators: Array<{ label: string; percent: number }>;
  benchmark: BenchmarkComparisonView;
  score_breakdown: ScoreBreakdownItem[];
  score_reason: string[];
  score_classification: string;
  insight_tags: InsightTagView[];
}

const DEFAULT_FINDING_FALLBACKS = [
  'Review actual versus TCP consumption variance from report.',
  'Assess idle period contribution to total port fuel use.',
  'Confirm cargo and manoeuvring efficiency against benchmarks.',
];

const DEFAULT_GUIDANCE_FALLBACKS = [
  'Review idle-period auxiliary consumption with ship staff.',
  'Compare TCP variance drivers before next port call.',
  'Monitor cargo and manoeuvring performance against baseline.',
];

function toText(value: unknown): string {
  return String(value ?? '').trim();
}

function splitTextToBullets(text: string, maxWords = 20): string[] {
  const cleaned = text
    .replace(/[•●▪]/g, '\n')
    .replace(/\r/g, '')
    .trim();
  if (!cleaned) {
    return [];
  }

  const parts = cleaned
    .split(/\n+|(?<=[.!?])\s+(?=[A-Z])/)
    .map((part) => part.replace(/^[-*•\d.)\s]+/, '').trim())
    .filter(Boolean);

  return parts
    .map((part) => limitWords(part, maxWords))
    .filter(Boolean);
}

function coerceBulletList(
  value: unknown,
  minItems: number,
  maxItems: number,
  maxWords: number,
  fallbacks: string[],
): string[] {
  let source: string[] = [];

  if (Array.isArray(value)) {
    if (value.length === 1 && countWordsInItems(value) > maxWords) {
      source = splitTextToBullets(toText(value[0]), maxWords);
    } else {
      source = value.map((item) => toText(item)).filter(Boolean);
    }
  } else if (typeof value === 'string' && value.trim()) {
    source = splitTextToBullets(value, maxWords);
  }

  let bullets = formatDashboardBullets(source, maxItems, maxWords);

  for (const fallback of fallbacks) {
    if (bullets.length >= minItems) {
      break;
    }
    const next = limitWords(fallback, maxWords);
    if (next && !bullets.includes(next)) {
      bullets.push(next);
    }
  }

  return bullets.slice(0, maxItems);
}

function countWordsInItems(items: unknown[]): number {
  return items
    .map((item) => toText(item))
    .join(' ')
    .split(/\s+/)
    .filter(Boolean).length;
}

function parseNumber(match: RegExpMatchArray | null, index = 1): number | null {
  if (!match?.[index]) {
    return null;
  }
  const value = Number(match[index]);
  return Number.isFinite(value) ? value : null;
}

function parseDirectedVariance(
  text: string,
  benchmark: 'tcp' | 'baseline',
): number | null {
  const label =
    benchmark === 'tcp'
      ? '(?:tcp|charter[\\s-]?party|cp)\\s*(?:benchmark|consumption)?'
      : '(?:technical\\s+)?(?:baseline|design)\\s*(?:benchmark|consumption)?';

  const patterns = [
    new RegExp(`(\\d+(?:\\.\\d+)?)\\s*%\\s*(below|under|above|over)\\s+(?:the\\s+)?${label}`, 'i'),
    new RegExp(`(?:was|is)\\s+(\\d+(?:\\.\\d+)?)\\s*%\\s*(below|under|above|over)\\s+(?:the\\s+)?${label}`, 'i'),
    new RegExp(`(below|under|above|over)\\s+(?:the\\s+)?${label}\\s+by\\s+(\\d+(?:\\.\\d+)?)\\s*%`, 'i'),
    new RegExp(`actual\\s+total\\s+consumption\\s+was\\s+(\\d+(?:\\.\\d+)?)\\s*%\\s*(below|under|above|over)\\s+${label}`, 'i'),
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (!match) {
      continue;
    }
    let pct: number;
    let direction: string;
    if (/below|under|above|over/i.test(match[1])) {
      direction = match[1].toLowerCase();
      pct = Number(match[2]);
    } else {
      pct = Number(match[1]);
      direction = (match[2] || '').toLowerCase();
    }
    if (!Number.isFinite(pct)) {
      continue;
    }
    if (/below|under/.test(direction)) {
      return -Math.abs(pct);
    }
    if (/above|over/.test(direction)) {
      return Math.abs(pct);
    }
  }
  return null;
}

export function parseInsightSignals(...sources: string[]): VesselInsightSignals {
  const text = sources.filter(Boolean).join('\n');

  const tcpVariancePct =
    parseDirectedVariance(text, 'tcp') ??
    parseNumber(text.match(/(?:vs|against|variance\s+(?:to|vs)?)\s*tcp[^.\n]{0,80}?([+-]?\d+(?:\.\d+)?)\s*%/i)) ??
    parseNumber(text.match(/tcp[^.\n]{0,100}?([+-]?\d+(?:\.\d+)?)\s*%/i));

  const baselineVariancePct =
    parseDirectedVariance(text, 'baseline') ??
    parseNumber(
      text.match(/(?:vs|against|variance\s+(?:to|vs)?)\s*(?:technical\s+)?baseline[^.\n]{0,80}?([+-]?\d+(?:\.\d+)?)\s*%/i),
    ) ??
    parseNumber(
      text.match(/(?:technical\s+)?baseline[^.\n]{0,100}?([+-]?\d+(?:\.\d+)?)\s*%/i),
    );

  const idleHours = parseNumber(
    text.match(/idle[^.\n]{0,80}?(\d+(?:\.\d+)?)\s*(?:hrs?|hours?)/i),
  );

  const portStayHours =
    parseNumber(text.match(/port\s+stay[^.\n]{0,80}?(\d+(?:\.\d+)?)\s*(?:hrs?|hours?)/i)) ??
    parseNumber(text.match(/stay\s+duration[^.\n]{0,80}?(\d+(?:\.\d+)?)\s*(?:hrs?|hours?)/i));

  const cargoVariancePct = parseNumber(
    text.match(/cargo[^.\n]{0,100}?([+-]?\d+(?:\.\d+)?)\s*%/i),
  );

  const manoeuvringVariancePct =
    parseNumber(
      text.match(/manoeuvr[^.\n]{0,100}?([+-]?\d+(?:\.\d+)?)\s*%/i),
    ) ??
    parseNumber(text.match(/maneuver[^.\n]{0,100}?([+-]?\d+(?:\.\d+)?)\s*%/i));

  return {
    tcpVariancePct,
    baselineVariancePct,
    idleHours,
    portStayHours,
    cargoVariancePct,
    manoeuvringVariancePct,
  };
}

export function deriveOperationalScore(
  signals: VesselInsightSignals,
  benchmark: BenchmarkComparisonView,
): number {
  return deriveScoreBreakdown(signals, benchmark).finalScore;
}

function resolveScoreReasons(
  payload: Record<string, unknown>,
  derived: string[],
): string[] {
  const fromPayload = payload['score_reason'];
  if (Array.isArray(fromPayload) && fromPayload.length) {
    const merged = fromPayload
      .map((item) => toText(item))
      .filter(Boolean)
      .slice(0, 5);
    if (merged.length) {
      return merged;
    }
  }
  return derived;
}

export function deriveRiskIndicators(
  signals: VesselInsightSignals,
  contentText: string,
): Array<{ label: string; percent: number }> {
  const lower = contentText.toLowerCase();
  let portDelay = 18;
  let fuelImpact = 16;
  let maintenance = 14;

  if (signals.portStayHours != null) {
    if (signals.portStayHours >= 96) portDelay += 28;
    else if (signals.portStayHours >= 72) portDelay += 20;
    else if (signals.portStayHours >= 48) portDelay += 12;
  }

  if (signals.idleHours != null) {
    if (signals.idleHours >= 48) portDelay += 22;
    else if (signals.idleHours >= 24) portDelay += 14;
    else if (signals.idleHours >= 12) portDelay += 8;
  }

  if (lower.includes('delay') || lower.includes('high duration')) {
    portDelay += 8;
  }

  if (signals.tcpVariancePct != null) {
    if (signals.tcpVariancePct >= 20) fuelImpact += 34;
    else if (signals.tcpVariancePct >= 10) fuelImpact += 24;
    else if (signals.tcpVariancePct >= 5) fuelImpact += 14;
    else if (signals.tcpVariancePct <= -5) fuelImpact -= 4;
  }

  if (lower.includes('exceed') && lower.includes('consumption')) {
    fuelImpact += 8;
  }

  if (signals.manoeuvringVariancePct != null) {
    if (signals.manoeuvringVariancePct >= 15) maintenance += 24;
    else if (signals.manoeuvringVariancePct >= 8) maintenance += 16;
    else if (signals.manoeuvringVariancePct >= 3) maintenance += 8;
  }

  if (signals.baselineVariancePct != null && signals.baselineVariancePct >= 10) {
    maintenance += 10;
  }

  if (lower.includes('maintenance') || lower.includes('machinery')) {
    maintenance += 6;
  }

  const clamp = (value: number) => Math.min(88, Math.max(12, Math.round(value)));

  return [
    { label: 'Port Delay Risk', percent: clamp(portDelay) },
    { label: 'Fuel Impact', percent: clamp(fuelImpact) },
    { label: 'Maintenance Risk', percent: clamp(maintenance) },
  ];
}

function expertPreview(text: string): string {
  return limitLines(limitWords(text, 35), 3);
}

function resolveRiskIndicators(
  payload: Record<string, unknown>,
  signals: VesselInsightSignals,
  contentText: string,
): Array<{ label: string; percent: number }> {
  const derived = deriveRiskIndicators(signals, contentText);

  if (!Array.isArray(payload['risk_indicators']) || !payload['risk_indicators'].length) {
    return derived;
  }

  const mapped = (payload['risk_indicators'] as Array<{ label?: string; percent?: number }>)
    .map((risk, index) => {
      const label = toText(risk.label) || derived[index]?.label || 'Risk';
      const percent = Math.round(Number(risk.percent) || 0);
      const derivedPercent = derived.find((d) => d.label === label)?.percent ??
        derived[index]?.percent ??
        18;
      const normalizedPercent =
        percent <= 0 ? derivedPercent : Math.min(88, Math.max(12, percent));
      return { label, percent: normalizedPercent };
    })
    .filter((risk) => risk.label);

  if (!mapped.length || mapped.every((risk) => risk.percent <= 0)) {
    return derived;
  }

  const labels = ['Port Delay Risk', 'Fuel Impact', 'Maintenance Risk'];
  return labels.map((label) => {
    const existing = mapped.find((risk) => risk.label === label);
    const fallback = derived.find((risk) => risk.label === label);
    return {
      label,
      percent: existing?.percent ?? fallback?.percent ?? 18,
    };
  });
}

function formatExpertField(value: unknown): string {
  const text = formatExpertComment(toText(value));
  if (!text || text === '—') {
    return formatExpertComment(EXPERT_COMMENT_FALLBACK);
  }
  return text;
}

export function normalizeVesselInsightsPayload(
  payload: Record<string, unknown>,
  extractedContext = '',
  extractionMeta?: Partial<ExtractionQualityMeta>,
): NormalizedVesselInsights {
  const contentText = [
    extractedContext,
    toText(payload['executive_summary']),
    JSON.stringify(payload['ai_insights'] ?? ''),
    JSON.stringify(payload['guidance'] ?? ''),
    toText(payload['chief_engineer']),
    toText(payload['technical_superintendent']),
    toText(payload['naval_architect']),
  ].join('\n');

  const signals = parseInsightSignals(extractedContext, contentText);
  const benchmark = parseBenchmarkComparison(
    extractedContext + '\n' + contentText,
    payload,
  );
  const scoreBreakdown = deriveScoreBreakdown(signals, benchmark);
  const meta: ExtractionQualityMeta = {
    pageCount: extractionMeta?.pageCount ?? 0,
    pagesIncluded: extractionMeta?.pagesIncluded ?? [],
    textChars: extractionMeta?.textChars ?? extractedContext.replace(/\s/g, '').length,
    usedOcr: extractionMeta?.usedOcr ?? false,
    markerCount:
      extractionMeta?.markerCount ??
      countMarkers(extractedContext + contentText),
  };

  const ai_insights = coerceBulletList(
    payload['ai_insights'],
    3,
    5,
    20,
    DEFAULT_FINDING_FALLBACKS,
  );

  const guidance = coerceBulletList(
    payload['guidance'],
    3,
    5,
    20,
    DEFAULT_GUIDANCE_FALLBACKS,
  );

  const executive_summary =
    formatExecutiveSummary(
      toText(payload['executive_summary']) || ai_insights.slice(0, 2).join(' '),
    ) ||
    formatExecutiveSummary(
      'Port performance reviewed against TCP and baseline benchmarks. Primary variance drivers identified from report data.',
    );

  const chief_engineer_full = formatExpertField(payload['chief_engineer']);
  const technical_superintendent_full = formatExpertField(
    payload['technical_superintendent'],
  );
  const naval_architect_full = formatExpertField(payload['naval_architect']);

  const operational_score = scoreBreakdown.finalScore;
  const score_reason = resolveScoreReasons(payload, scoreBreakdown.scoreReason);
  const score_classification = scoreBreakdown.classification;
  const ai_confidence = deriveAiConfidence(meta);
  const risk_indicators = resolveRiskIndicators(payload, signals, contentText);
  const insight_tags = deriveInsightTags(
    signals,
    benchmark,
    toText(payload['operation']),
  );

  return {
    executive_summary,
    ai_insights,
    guidance,
    chief_engineer: expertPreview(chief_engineer_full),
    chief_engineer_full,
    technical_superintendent: expertPreview(technical_superintendent_full),
    technical_superintendent_full,
    naval_architect: expertPreview(naval_architect_full),
    naval_architect_full,
    operational_score,
    ai_confidence,
    risk_indicators,
    benchmark,
    score_breakdown: scoreBreakdown.items,
    score_reason,
    score_classification,
    insight_tags,
  };
}

function countMarkers(text: string): number {
  const markers = [/idle/i, /cargo/i, /manoeuvr|maneuver/i, /tcp/i, /baseline/i, /consumption/i, /benchmark/i];
  return markers.filter((rx) => rx.test(text)).length;
}
